import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { useState } from "react";
import { useToast } from "@/hooks/useToast";
import { useAppContext } from "@/hooks/useAppContext";
import type { ScryfallCard } from "@/types/scryfall";
import { parseCardList, fetchCardsFromIdentifiers } from "@/lib/card-importer";
import { ListLoadingIndicator } from "./ListLoadingIndicator";

interface DeckImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setCards: (cards: ScryfallCard[]) => void;
  setSideboardCards: (cards: ScryfallCard[]) => void;
}

export function DeckImportDialog({ open, onOpenChange, setCards, setSideboardCards }: DeckImportDialogProps) {
  const [list, setList] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const { toast } = useToast();
  const { config } = useAppContext();

  const handleImport = async () => {
    setIsImporting(true);
    try {
      const sections = parseCardList(list);
      const mainIdentifiers = sections.main;
      const sideboardIdentifiers = sections.sideboard;

      const mainCards = await fetchCardsFromIdentifiers(mainIdentifiers, config.scryfallApiUrl);
      const sideboardCards = await fetchCardsFromIdentifiers(sideboardIdentifiers, config.scryfallApiUrl);

      setCards(mainCards);
      setSideboardCards(sideboardCards);

      toast({ title: "List imported successfully!" });
      setList(""); // Clear the input for next time
      onOpenChange(false);
    } catch {
      toast({ title: "Failed to import list", variant: "destructive" });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <>
      {isImporting && (
        <div className="fixed inset-0 bg-background/60 backdrop-blur-md z-50 flex items-center justify-center">
          <ListLoadingIndicator
            isImporting={true}
            variant="overlay"
            title="Take your time."
          />
        </div>
      )}

      <Dialog open={open && !isImporting} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Import Card List</DialogTitle>
            <DialogDescription>
              Paste your card list below. Each line should be in the format "1 Card Name" or "Card Name". Use "Sideboard:" or "SIDEBOARD:" on a separate line to denote sideboard cards.
            </DialogDescription>
          </DialogHeader>

          <Textarea
            value={list}
            onChange={(e) => setList(e.target.value)}
            placeholder={`1 Lightning Bolt\n\nSIDEBOARD:\n1 Rest in Peace\n...\n\nOR\n\n1 Horobi, Death's Wail (CHK) 117 *F*\n1 Animate Dead (PD3) 16 *F*\n...`}
            className="h-64"
          />

          <DialogFooter>
            <Button onClick={handleImport} disabled={isImporting}>
              {isImporting ? "Importing..." : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}